<template>
  <FormQuestion
    v-if="modelValue"
    @back="$emit('back')"
    @next="$emit('next')"
    :class="landscape ? 'my-0' : 'mb-6'"
    :color="color"
    :conditional="question.conditions.length > 0"
    :conditions-failed="question.conditions_failed"
    :dense="dense"
    :elevation="elevation"
    :header="header"
    :hide-actions="hideActions"
    :income="true"
    :landscape="landscape"
    :liquid-rendering-enabled="liquidRenderingEnabled"
    :mandatory="question.mandatory"
    :model-value="modelValue"
    :next-disabled="nextDisabled"
    :outlined="outlined"
    :paddingless="paddingless"
    :processing="processing"
    :published="question.published"
    :reversible="reversible"
    :show-indicators="showIndicators"
    :subtitle="question.subtitle"
    :tile="tile"
    :title="dynamicTitle"
    :very-dense="veryDense"
    data-testid="income-question"
  >
    <template v-if="!hideField">
      <template v-if="questionType === INCOME_TYPE.RANGE_SELECTION">
        <v-radio-group
          @update:model-value="updateRangeSelection"
          :disabled="readonly"
          :model-value="currentSelection"
          :required="question.mandatory"
          class="mt-0"
        >
          <template
            v-for="(rangeLabel, rangeKey) in incomeQuestionData?.ranges || {}"
            :key="rangeKey"
          >
            <v-radio
              :disabled="readonly"
              :label="rangeLabel"
              :value="rangeLabel"
              class="mb-2"
              color="primary"
              data-testid="income-range-option"
            />
          </template>
        </v-radio-group>
      </template>

      <template v-if="questionType === INCOME_TYPE.BOOLEAN_CUTOFF">
        <v-radio-group
          @update:model-value="handleBooleanUpdate"
          :disabled="readonly"
          :model-value="currentSelection"
          :required="question.mandatory"
          class="mt-0"
        >
          <v-radio
            :disabled="readonly"
            :label="$bct(question.true_label || 'Yes')"
            :value="true"
            class="mb-2"
            color="primary"
            data-testid="income-boolean-yes"
          />
          <v-radio
            :disabled="readonly"
            :label="$bct(question.false_label || 'No')"
            :value="false"
            class="mb-2"
            color="primary"
            data-testid="income-boolean-no"
          />
        </v-radio-group>
      </template>
    </template>
  </FormQuestion>
</template>

<script setup>
import FormQuestion from '@/shared/components/form/FormQuestion.vue';
import { INCOME_TYPE, INCOME_FIELD_MAPPING } from '@/shared/constants/incomeQuestions';
import useApi from '@/shared/composables/useApi';

const $bct = inject('translator');
const { api: Api } = useApi();

const emit = defineEmits(['back', 'change', 'change:input', 'next', 'update:modelValue']);

const modelValue = defineModel({ default: () => ({ custom: {} }), type: Object });

const props = defineProps({
  color: { default: 'transparent', type: String },
  dense: { default: false, type: Boolean },
  elevation: { default: 0, type: Number },
  header: { type: String, default: '' },
  hideActions: { type: Boolean, default: false },
  hideField: { type: Boolean, default: false },
  landscape: { type: Boolean, default: false },
  liquidRenderingEnabled: { type: Boolean, default: true },
  outlined: { type: Boolean, default: false },
  paddingless: { type: Boolean, default: false },
  processing: { default: false, type: Boolean },
  previewMode: { type: Boolean, default: false },
  question: { default: () => ({}), type: Object },
  readonly: { type: Boolean, default: false },
  reversible: { type: Boolean, default: true },
  showIndicators: { type: Boolean, default: false },
  tile: { type: Boolean, default: false },
  veryDense: { type: Boolean, default: false },
});

const incomeQuestionData = ref(null);
const isLoadingIncomeData = ref(false);

const fieldMappings = {
  [INCOME_TYPE.RANGE_SELECTION]: INCOME_FIELD_MAPPING.SELECTED_AMOUNTS,
  [INCOME_TYPE.BOOLEAN_CUTOFF]: INCOME_FIELD_MAPPING.BOOLEAN_ANSWER,
};

// Computed properties (alphabetically)
const currentRanges = computed(() => incomeQuestionData.value?.ranges || {});

const currentSelection = computed(() => {
  const value = getCurrentValue(currentValueFieldKey.value);
  if (value === null || value === undefined) return null;
  if (!householdSizesMatch.value) return null;

  if (questionType.value === INCOME_TYPE.RANGE_SELECTION) {
    return isValidRangeAmount(value) ? value : null;
  }

  return value;
});

const currentValueFieldKey = computed(() => fieldMappings[questionType.value]);

const dynamicTitle = computed(() => {
  const generatedTitle = incomeQuestionData.value?.title;
  return generatedTitle || props.question.title;
});

const householdSizesMatch = computed(() => {
  const householdSizeWhenAnswered = getCurrentValue(INCOME_FIELD_MAPPING.HOUSEHOLD_SIZE_SNAPSHOT);
  const currentHouseholdSize = modelValue.value?.household_size;
  return householdSizeWhenAnswered === currentHouseholdSize;
});

const nextDisabled = computed(() => props.question.mandatory && !currentSelection.value);

const questionType = computed(() => props.question.income_question_type);

// Helper functions (alphabetically)
const findPercentageKeyForAmount = (amount) => {
  return amount && Object.entries(currentRanges.value).find(([_, amt]) => amt === amount)?.[0];
};

const isValidRangeAmount = (amount) => {
  return amount && Object.values(currentRanges.value).includes(amount);
};

const fetchIncomeQuestionData = async () => {
  // When questions aren't using api-side filtering and sorting, we need to fetch the income question
  // from the question set endpoint to get the right data into it
  if (isLoadingIncomeData.value) return;

  isLoadingIncomeData.value = true;

  const params = {
    eligibility: props.question.eligibility || false,
    owner_id: props.question.owner_id,
    owner_type: props.question.owner_type,
    synced_model: props.question.synced_model,
    draft_data: props.previewMode ? { custom: {}, household_size: 4 } : modelValue.value,
    include_unpublished: props.previewMode,
  };

  const response = await Api.shared.questionSet.index(params);

  if (!response?.data) {
    isLoadingIncomeData.value = false;
    return;
  }

  const questionWithData = response.data.find((q) => q.id === props.question.id);

  if (questionWithData?.income_question_data) {
    incomeQuestionData.value = questionWithData.income_question_data;
  }

  isLoadingIncomeData.value = false;
};

const getCurrentValue = (mapsToKey) => {
  const attribute = props.question.synced_attributes?.find((attr) => attr.maps_to === mapsToKey);
  if (!attribute) return null;
  if (!modelValue.value) return null;

  let value;
  if (attribute.name?.includes('custom.')) {
    value = modelValue.value.custom?.[attribute.name.split('.')[1]];
  } else {
    value = modelValue.value[attribute.name];
  }

  if (value === undefined) return null;

  return value;
};

const handleBooleanUpdate = (value) => {
  updateValue(currentValueFieldKey.value, value);
  updateHouseholdSizeSnapshot();
};

const initializeIncomeData = () => {
  if (props.question.income_question_data) {
    incomeQuestionData.value = props.question.income_question_data;
    return;
  }

  if (isLoadingIncomeData.value) return;
  if (!props.question.income_question_type) return;
  if (!props.question.owner_type) return;
  if (!props.question.owner_id) return;
  if (!props.question.synced_model) return;

  fetchIncomeQuestionData();
};

const updateHouseholdSizeSnapshot = () => {
  updateValue(INCOME_FIELD_MAPPING.HOUSEHOLD_SIZE_SNAPSHOT, modelValue.value.household_size);
};

const updateRangeSelection = (rangeAmount) => {
  updateValue(INCOME_FIELD_MAPPING.SELECTED_AMOUNTS, rangeAmount);
  updateValue(INCOME_FIELD_MAPPING.SELECTED_PERCENTAGES, findPercentageKeyForAmount(rangeAmount));
  updateHouseholdSizeSnapshot();
};

const updateValue = (mapsToKey, value) => {
  const attribute = props.question.synced_attributes?.find((attr) => attr.maps_to === mapsToKey);
  if (!attribute) return;

  if (attribute.name?.includes('custom.')) {
    if (!modelValue.value.custom) modelValue.value.custom = {};
    modelValue.value.custom[attribute.name.split('.')[1]] = value;
  } else {
    modelValue.value[attribute.name] = value;
  }

  return emit('change', {
    attribute,
    value,
  });
};

onMounted(initializeIncomeData);

watch([() => props.question, () => modelValue.value?.household_size], initializeIncomeData);
</script>
